import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileSpreadsheet, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useModalStore } from "@/contexts/ModalContext";

interface LabResult {
  id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string;
  created_at: string;
  subcategory_id: string | null;
  lab_subcategories?: {
    name: string;
    category_id: string;
  } | null;
}

interface RecordResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  sessionFileName?: string;
  supabaseClient?: SupabaseClient<Database>;
}

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "-";
  }
};

const getCategoryName = (subcategory: { name: string } | null | undefined) => {
  return subcategory?.name || "-";
};

const formatReference = (low: number | null, high: number | null) => {
  if (!low && !high) return "-";
  if (low && high) return `${low} - ${high}`;
  if (low) return `> ${low}`;
  if (high) return `< ${high}`;
  return "-";
};

export function RecordResultsModal({
  isOpen,
  onClose,
  sessionId,
  sessionFileName,
  supabaseClient,
}: RecordResultsModalProps) {
  const [labResults, setLabResults] = useState<LabResult[]>([]);
  const [loading, setLoading] = useState(true);
  const { registerModal, unregisterModal } = useModalStore();
  const client = supabaseClient ?? supabase;

  useEffect(() => {
    if (isOpen && sessionId) {
      loadResults();
    } else {
      setLabResults([]);
      setLoading(true);
    }
  }, [isOpen, sessionId]);

  // Registrar/desregistrar modal no store
  useEffect(() => {
    const modalId = `results-modal-${sessionId}`;
    if (isOpen) {
      registerModal(modalId);
    } else {
      unregisterModal(modalId);
    }
    return () => {
      unregisterModal(modalId);
    };
  }, [isOpen, sessionId, registerModal, unregisterModal]);

  async function loadResults() {
    setLoading(true);
    try {
      const { data, error } = await client
        .from("lab_results_onboarding")
        .select(`
          *,
          lab_subcategories:subcategory_id (
            name,
            category_id
          )
        `)
        .eq("source_session_id", sessionId)
        .order("collected_at", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error loading lab results:", error);
        setLabResults([]);
      } else {
        setLabResults(data || []);
      }
    } catch (error) {
      console.error("Error loading lab results:", error);
      setLabResults([]);
    } finally {
      setLoading(false);
    }
  }

  const sortedResults = [...labResults].sort((a, b) => 
    new Date(b.collected_at || b.created_at).getTime() - 
    new Date(a.collected_at || a.created_at).getTime()
  );

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      modal={true}
    >
      <DialogContent 
        className="max-w-6xl max-h-[90vh]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Resultados do Registro
            {sessionFileName && (
              <span className="text-sm font-normal text-muted-foreground">
                - {sessionFileName}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : labResults.length === 0 ? (
            <Alert>
              <FileSpreadsheet className="h-4 w-4" />
              <AlertDescription>
                Nenhum resultado de exame encontrado para este registro.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Versão Desktop - Tabela */}
              <div className="hidden lg:block">
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Data Coleta</TableHead>
                        <TableHead>Exame</TableHead>
                        <TableHead>Valor</TableHead>
                        <TableHead>Unidade</TableHead>
                        <TableHead>Referência</TableHead>
                        <TableHead>Categoria</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedResults.map((result, index) => (
                        <TableRow key={result.id}>
                          <TableCell className="font-mono text-muted-foreground">
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            {formatDate(result.collected_at || result.created_at)}
                          </TableCell>
                          <TableCell className="font-medium">
                            {result.test_name}
                          </TableCell>
                          <TableCell>
                            {result.value !== null ? result.value : "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {result.unit || "-"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatReference(result.ref_low, result.ref_high)}
                          </TableCell>
                          <TableCell>
                            {getCategoryName(result.lab_subcategories)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>

              {/* Versão Mobile - Cards */}
              <div className="lg:hidden">
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {sortedResults.map((result, index) => (
                      <div key={result.id} className="border rounded-lg p-4">
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-mono text-xs text-muted-foreground mb-1">
                                #{index + 1}
                              </div>
                              <div className="font-medium text-sm">{result.test_name}</div>
                              <div className="text-xs text-muted-foreground">
                                {formatDate(result.collected_at || result.created_at)}
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <div className="text-muted-foreground">Valor</div>
                              <div className="font-medium">
                                {result.value !== null ? result.value : "-"}
                              </div>
                            </div>
                            <div>
                              <div className="text-muted-foreground">Unidade</div>
                              <div className="font-medium">{result.unit || "-"}</div>
                            </div>
                            <div>
                              <div className="text-muted-foreground">Referência</div>
                              <div className="font-medium">
                                {formatReference(result.ref_low, result.ref_high)}
                              </div>
                            </div>
                            <div>
                              <div className="text-muted-foreground">Categoria</div>
                              <div className="font-medium">
                                {getCategoryName(result.lab_subcategories)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
              
              <div className="mt-4 text-sm text-muted-foreground">
                📝 Total: {labResults.length} resultado(s)
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

